import { Dropdown, MenuItem } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import Flex from '../generic/Flex';
import { icons } from '../generic/Icon/icons';
import { IconText } from '../generic/Icon/index';
import { NEW_TAB_PROPS } from '../generic/Link/Link';
import RouterLink from '../generic/Link/RouterLink';
import { shouldDisplayOrganizationPortalSettings } from '../settings/shared/permissions';
import { PORTAL_SETTINGS_PATH } from '../shared/publicPortal';
import usePortalUrl from '../shared/publicPortal/hooks/usePortalUrl';

import MenuItemLink from './MenuItemLink';

type Props = {
  routerPush: InjectedRouter['push'];
  pathname: string;
};

const SBOMNavText = () => (
  <Flex gap="4px">
    <span>SBOM Portal</span>
  </Flex>
);

/**
 * Displays the SBOM portal link. If the user has access to the portal settings,
 * it will display a dropdown with a link to the portal settings and the portal.
 */
const SBOMPortalLinks: VoidFunctionComponent<Props> = ({ pathname, routerPush }) => {
  const isOnPortalPage = pathname.startsWith(PORTAL_SETTINGS_PATH);
  const { url: portalUrl } = usePortalUrl();
  const isAllowToViewPortalSettings = shouldDisplayOrganizationPortalSettings();

  // Render a dropdown to include a link to the portal and a link to the portal settings
  if (isAllowToViewPortalSettings && portalUrl) {
    return (
      <li className="nav-right link-dropdown-right" key="sbom-portal-links">
        <Dropdown id="sbom-portal-dropdown" pullRight>
          <Dropdown.Toggle useAnchor />
          <Dropdown.Menu className="nav-portal-dropdown">
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <MenuItem href={portalUrl} {...NEW_TAB_PROPS}>
              <IconText text="View Portal" icon={icons.externalLink} alignIcon="right" marginAmount="36px" />
            </MenuItem>
            <MenuItemLink key="portal" eventKey={PORTAL_SETTINGS_PATH} routerPush={routerPush}>
              Portal Settings
            </MenuItemLink>
          </Dropdown.Menu>
          <RouterLink className={isOnPortalPage ? 'active' : ''} activeClassName="active" to={portalUrl} openInNewTab>
            <SBOMNavText />
          </RouterLink>
        </Dropdown>
      </li>
    );
  }

  // Render a link to the portal settings
  if (isAllowToViewPortalSettings) {
    return (
      <li className="nav-right link-dropdown-right" key="sbom-portal-links">
        <RouterLink className={isOnPortalPage ? 'active' : ''} activeClassName="active" to={PORTAL_SETTINGS_PATH}>
          <SBOMNavText />
        </RouterLink>
      </li>
    );
  }

  // Render a link to the portal
  if (portalUrl) {
    return (
      <li className="nav-right link-dropdown-right" key="sbom-portal-links">
        <RouterLink className={isOnPortalPage ? 'active' : ''} activeClassName="active" to={portalUrl} openInNewTab>
          <SBOMNavText />
        </RouterLink>
      </li>
    );
  }

  // Render nothing if the user doesn't have access to the portal settings and the portal link is not available
  return null;
};

export default SBOMPortalLinks;
